import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Spinner } from '../Spinner';
import { FaIcon } from '../FaIcon';
import { ClientDashboardColumnsSetsApi } from '../../api/clientDashboardColumnsSets';
import { getColumnHeaderInformation } from '../dashboard/columns';

const PREVIEW_CHIPS_LIMIT = 4;

function prettyColumnId(columnId) {
    if (!columnId) return '';
    if (columnId.startsWith('FLEX_INDICATOR||')) {
        const parts = columnId.replace('FLEX_INDICATOR||', '').split('||');
        return parts.join(' · ').replace(/_/g, ' ').toLowerCase();
    }
    return columnId.replace(/_COLUMN$/, '').replace(/_/g, ' ').toLowerCase();
}

/**
 * Modal para asignar / desasignar Sets de columnas (UserDashboardColumnsSettings del
 * agency user) al portal del cliente.
 *
 * No crea sets — los sets se crean en el Tablero general (modal Configurar columnas).
 *
 * Props:
 *  - isOpen, toggle
 *  - clientId
 *  - currentAssignments: array de assignments actuales del cliente
 *    `[{id, columns_settings_id, name, active_columns, order}, ...]`
 *  - onSaved(): callback cuando el diff se aplicó.
 */
export function AssignColumnSetToPortalModal({ isOpen, toggle, clientId, currentAssignments, onSaved }) {
    const { t } = useTranslation();
    const [agencySets, setAgencySets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedSetIds, setSelectedSetIds] = useState(new Set());

    // Mapa: columns_settings_id → assignment_id (para desasignar luego).
    const assignmentByColumnsSettingsId = useMemo(() => {
        const m = new Map();
        (currentAssignments || []).forEach((a) => {
            m.set(a.columns_settings_id, a.id);
        });
        return m;
    }, [currentAssignments]);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        // Reset selected state al estado actual de assignments del cliente.
        setSelectedSetIds(new Set(assignmentByColumnsSettingsId.keys()));

        ClientDashboardColumnsSetsApi.listAgencyUserSets()
            .then((response) => {
                if (response && response.status === 'OK' && Array.isArray(response.data?.column_sets)) {
                    // Filtramos el set "unnamed" temporal — no es un set guardado real.
                    const named = response.data.column_sets.filter(
                        (s) => s.name && s.name !== 'UNNAMED_UNSAVED_COLUMNS_SET'
                    );
                    setAgencySets(named);
                } else {
                    setAgencySets([]);
                }
                setLoading(false);
            })
            .catch(() => {
                setAgencySets([]);
                setLoading(false);
            });
    }, [isOpen, assignmentByColumnsSettingsId]);

    const toggleSelected = (setId) => {
        setSelectedSetIds((prev) => {
            const next = new Set(prev);
            if (next.has(setId)) next.delete(setId);
            else next.add(setId);
            return next;
        });
    };

    const diff = useMemo(() => {
        const currentlyAssigned = new Set(assignmentByColumnsSettingsId.keys());
        const toAssign = [...selectedSetIds].filter((id) => !currentlyAssigned.has(id));
        const toUnassign = [...currentlyAssigned]
            .filter((id) => !selectedSetIds.has(id))
            .map((id) => assignmentByColumnsSettingsId.get(id));
        return { toAssign, toUnassign };
    }, [selectedSetIds, assignmentByColumnsSettingsId]);

    const hasChanges = diff.toAssign.length > 0 || diff.toUnassign.length > 0;

    const handleSave = async () => {
        if (!hasChanges) {
            toggle();
            return;
        }
        setSaving(true);
        try {
            // Desasignar primero, asignar después — orden seguro para evitar conflictos
            // de unique_together si el user toggleó algo al mismo set.
            for (const assignmentId of diff.toUnassign) {
                await ClientDashboardColumnsSetsApi.unassignSet(clientId, assignmentId);
            }
            for (const columnsSettingsId of diff.toAssign) {
                await ClientDashboardColumnsSetsApi.assignSet(clientId, columnsSettingsId);
            }
            toast.success(t('client_portal_dashboard.assignments_saved'));
            onSaved && onSaved();
            toggle();
        } catch (e) {
            toast.error(t('client_portal_dashboard.save_error'));
        } finally {
            setSaving(false);
        }
    };

    const columnHeaderInfo = useMemo(() => getColumnHeaderInformation(t), [t]);
    const totalSets = agencySets.length;
    const selectedCount = selectedSetIds.size;

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                <FaIcon iconName="table-columns" />
                {' '}{t('client_portal_dashboard.assign_modal_title')}
            </ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    <p className="assign-set-modal-hint">
                        {t('client_portal_dashboard.assign_modal_hint')}
                    </p>

                    {loading ? (
                        <div className="box-loader">
                            <Spinner color="positive" />
                        </div>
                    ) : agencySets.length === 0 ? (
                        <div className="assign-set-modal-empty">
                            <FaIcon iconName="circle-info" className="assign-set-modal-empty-icon" />
                            <p className="assign-set-modal-empty-title">
                                {t('client_portal_dashboard.assign_modal_empty_title')}
                            </p>
                            <p className="assign-set-modal-empty-body">
                                {t('client_portal_dashboard.assign_modal_empty_body')}
                            </p>
                            <Link to="/" className="btn -alt" onClick={toggle}>
                                <FaIcon iconName="arrow-up-right-from-square" />
                                {' '}{t('client_portal_dashboard.go_to_dashboard')}
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="assign-set-modal-toolbar">
                                <span className="assign-set-modal-counter">
                                    <span className="assign-set-modal-counter-strong">{selectedCount}</span>
                                    {' '}/ {totalSets}{' '}{t('client_portal_dashboard.assign_modal_selected')}
                                </span>
                            </div>
                            <ul className="assign-set-modal-list">
                                {agencySets.map((s, idx) => {
                                    const isSelected = selectedSetIds.has(s.id);
                                    const otherAssignmentsCount = Math.max(
                                        0,
                                        (s.portal_assignments_count || 0) - (assignmentByColumnsSettingsId.has(s.id) ? 1 : 0)
                                    );
                                    const colsCount = (s.columns || []).length;
                                    const previewLabels = (s.columns || [])
                                        .slice(0, PREVIEW_CHIPS_LIMIT)
                                        .map((id) => (columnHeaderInfo[id] && columnHeaderInfo[id].name) || prettyColumnId(id));
                                    const previewOverflow = Math.max(0, colsCount - PREVIEW_CHIPS_LIMIT);
                                    return (
                                        <li
                                            key={s.id}
                                            className={`assign-set-modal-item ${isSelected ? '-selected' : ''}`}
                                            style={{ animationDelay: `${Math.min(idx, 8) * 28}ms` }}
                                            onClick={() => toggleSelected(s.id)}
                                            tabIndex={0}
                                            role="checkbox"
                                            aria-checked={isSelected}
                                            onKeyDown={(e) => {
                                                if (e.key === ' ' || e.key === 'Enter') {
                                                    e.preventDefault();
                                                    toggleSelected(s.id);
                                                }
                                            }}
                                        >
                                            <span className={`assign-set-modal-checkbox ${isSelected ? '-checked' : ''}`}>
                                                {isSelected && <FaIcon iconName="check" />}
                                            </span>
                                            <span className="assign-set-modal-item-main">
                                                <span className="assign-set-modal-item-header">
                                                    <span className="assign-set-modal-item-name">{s.name}</span>
                                                    <span className="assign-set-modal-item-count">
                                                        {colsCount}{' '}
                                                        {t('client_portal_dashboard.columns_count_short')}
                                                    </span>
                                                    {otherAssignmentsCount > 0 && (
                                                        <span
                                                            className="assign-set-modal-item-portals-badge"
                                                            title={t(
                                                                otherAssignmentsCount === 1
                                                                    ? 'client_portal_dashboard.also_in_n_portals_one'
                                                                    : 'client_portal_dashboard.also_in_n_portals_other',
                                                                { count: otherAssignmentsCount }
                                                            )}
                                                        >
                                                            <FaIcon iconName="user-group" />
                                                            {' '}{otherAssignmentsCount}
                                                        </span>
                                                    )}
                                                </span>
                                                {previewLabels.length > 0 && (
                                                    <span className="assign-set-modal-item-preview">
                                                        {previewLabels.map((label, i) => (
                                                            <span key={`${label}-${i}`} className="assign-set-modal-item-chip">
                                                                {label}
                                                            </span>
                                                        ))}
                                                        {previewOverflow > 0 && (
                                                            <span className="assign-set-modal-item-chip -more">
                                                                +{previewOverflow}
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle} disabled={saving}>
                    {t('client_portal_dashboard.cancel')}
                </Button>
                {saving ? (
                    <Spinner color="positive small" />
                ) : (
                    <button
                        type="button"
                        className="btn -alt"
                        onClick={handleSave}
                        disabled={!hasChanges && agencySets.length > 0}
                    >
                        <FaIcon iconName="check" />
                        {' '}{t('client_portal_dashboard.save_changes')}
                    </button>
                )}
            </ModalFooter>
        </Modal>
    );
}
